import React from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    LinearProgress,
    Typography,
} from '@material-ui/core';

import * as bucketSelectors from '../../selectors/bucketSelectors';
import InfiniteScroll from 'react-infinite-scroll-component';
import * as organizationConstants from '../../constants/organizations';
import * as organizationSelectors from '../../selectors/organizationSelectors';

const Message = ({ children, classes }) => {
    return <div className={classes.message}>{children}</div>;
};

class MemberToBuckets extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            bucketNumber: props.buckets.length,
            filterName: props.filterName,
            fetchingAll: false,
            page: 0,
        };

        props.searchBuckets({
            organizationId: props.organizationId,
            resetList: true,
        });
    }

    handleToggle = value => {
        const { checkBuckets, bucketIds } = this.props;
        const currentIndex = bucketIds.indexOf(value);
        const newChecked = [...bucketIds];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        checkBuckets(newChecked);
    };

    onSelectAll = () => {
        const { buckets, checkBuckets } = this.props;

        this.setState({ fetchingAll: true }, () => {
            this.props.searchBuckets({
                organizationId: this.props.organizationId,
                filterName: this.props.filterName,
                fetchAll: true,
                resetList: true,
            });
        });

        const bucketIds = buckets.map(bucket => {
            return bucket.id;
        });
        checkBuckets(bucketIds);
    };

    static getDerivedStateFromProps(props, state) {
        if (props.filterName !== state.filterName) {
            return {
                filterName: props.filterName,
                page: 0,
            };
        }

        if (state.bucketNumber !== props.buckets.length) {
            if (state.fetchingAll) {
                const bucketIdMapping = props.buckets.map(bucket => {
                    return bucket.id;
                });
                props.checkBuckets(bucketIdMapping);
            }

            return {
                fetchingAll: false,
                bucketNumber: props.buckets.length,
            };
        }

        return null;
    }

    onLoadMore = () => {
        const { page } = this.state;

        this.props.searchBuckets({
            organizationId: this.props.organizationId,
            filterName: this.props.filterName,
            page: page + 1,
        });

        this.setState({ page: page + 1 });
    };

    render() {
        const {
            isLoadingBuckets,
            areThereMorePages,
            buckets,
            bucketIds,
            filterName,
            classes,
        } = this.props;

        let message, loader;
        switch (true) {
            case isLoadingBuckets:
                loader = <LinearProgress />;
                break;
            case !buckets.length && !!filterName:
                message = <Message classes={classes}>Nothing found.</Message>;
                break;
            case !buckets.length:
                message = (
                    <Message classes={classes}>
                        <img alt="information" src="/assets/info_bubble.svg" />
                        Oooops, seems like there are no projects.
                    </Message>
                );
                break;
            default:
                message = undefined;
        }

        const bucketsRender = buckets.map(bucket => {
            const labelId = `checkbox-list-label-${bucket.id}`;
            return (
                <ListItem
                    className={classes.listItem}
                    key={bucket.id}
                    role={undefined}
                    dense
                    button
                    onClick={() => this.handleToggle(bucket.id)}
                >
                    <ListItemIcon>
                        <Checkbox
                            edge="start"
                            checked={bucketIds.indexOf(bucket.id) !== -1}
                            tabIndex={-1}
                            disableRipple
                            color="primary"
                            inputProps={{ 'aria-labelledby': labelId }}
                        />
                    </ListItemIcon>
                    <ListItemText
                        className={classes.listItemText}
                        id={labelId}
                        primary={bucket.name}
                    />
                </ListItem>
            );
        });

        return (
            <List className={classes.wrapper}>
                {loader}
                {message}
                {buckets && buckets.length > 0 && (
                    <ListItem
                        className={classes.listItem}
                        key={'select-all'}
                        role={undefined}
                        dense
                        button
                        onClick={this.onSelectAll}
                    >
                        <ListItemIcon>
                            <Checkbox
                                edge="start"
                                checked={buckets.length === bucketIds.length}
                                tabIndex={-1}
                                disableRipple
                                color="primary"
                                inputProps={{
                                    'aria-labelledby':
                                        'checkbox-list-label-select-all',
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            className={classes.listItemText}
                            id={'checkbox-list-label-select-all'}
                            primary={
                                <Typography
                                    component="span"
                                    variant="body2"
                                    className={classes.selectAllListItemText}
                                    color="textPrimary"
                                >
                                    Select All
                                </Typography>
                            }
                        />
                    </ListItem>
                )}
                <InfiniteScroll
                    dataLength={buckets.length}
                    next={this.onLoadMore}
                    hasMore={areThereMorePages}
                    userWindow={false}
                    loader={<LinearProgress />}
                    scrollableTarget="scroll-wrapper"
                >
                    <div className={classes.list}>{bucketsRender}</div>
                </InfiniteScroll>
            </List>
        );
    }
}

const style = ({ spacing }) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: spacing.unit * 4,
    },
    list: {
        width: 400,
    },
    listItem: {
        padding: 0,
    },
    listItemText: {
        paddingLeft: 0,
    },
    selectAllListItemText: {
        paddingLeft: 0,
        fontWeight: '700',
    },
    message: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
    },
});

MemberToBuckets.propTypes = {
    buckets: PropTypes.array,
    bucketIds: PropTypes.array,
    checkBuckets: PropTypes.func,
    isLoadingBuckets: PropTypes.bool,
    areThereMorePages: PropTypes.bool,
    searchBuckets: PropTypes.func,
    organizationId: PropTypes.number,
    filterName: PropTypes.string,
};

MemberToBuckets.defaultProps = {
    buckets: [],
    bucketIds: [],
    isLoadingBuckets: true,
};

const mapStateToProps = state => ({
    buckets: bucketSelectors.getBuckets(state),
    isLoadingBuckets: bucketSelectors.getIsLoadingBuckets(state),
    areThereMorePages: bucketSelectors.areThereMorePages(state),
    organizationId: organizationSelectors.getCurrentOrganizationId(state),
    filterName: bucketSelectors.getOrganizationBucketsFilterName(state),
});

const mapDispatchToProps = dispatch => ({
    searchBuckets: ({
        organizationId,
        filterName,
        page,
        resetList,
        fetchAll,
    }) => {
        dispatch({
            type: organizationConstants.FETCH_ORGANIZATION_BUCKETS_REQUEST,
            payload: {
                organizationId,
                filterName,
                page,
                resetList,
                fetchAll,
            },
        });
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(injectSheet(style)(MemberToBuckets));



// WEBPACK FOOTER //
// ./src/components/members/MemberToBuckets.js